import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Minus, Equal } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SectionDiff, DiffLine } from '@/lib/writer/diff';

interface Props {
  sections: SectionDiff[];
  /** Hide equal-only sections by default; toggle exposed to the user. */
  hideUnchanged?: boolean;
  /** Optional caption shown in the toolbar (e.g. "v3 → v4"). */
  caption?: string;
}

/**
 * Renders a list of per-section unified diffs. Deliberately dependency-free
 * (just Tailwind + lucide) — no monaco, no react-diff-viewer — so it stays
 * lightweight on the writer canvas.
 */
const WriterDiffView: React.FC<Props> = ({ sections, hideUnchanged: hideDefault = true, caption }) => {
  const [hideUnchanged, setHideUnchanged] = useState(hideDefault);
  const visible = hideUnchanged ? sections.filter((s) => !s.unchanged) : sections;

  const totalAdded = sections.reduce((n, s) => n + s.added, 0);
  const totalRemoved = sections.reduce((n, s) => n + s.removed, 0);
  const allUnchanged = totalAdded === 0 && totalRemoved === 0;

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-border/60 text-xs">
        {caption && (
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {caption}
          </span>
        )}
        <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 tabular-nums">
          <Plus className="h-3 w-3" />
          {totalAdded}
        </span>
        <span className="flex items-center gap-1 text-destructive tabular-nums">
          <Minus className="h-3 w-3" />
          {totalRemoved}
        </span>
        <label className="ml-auto flex items-center gap-1.5 text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-3 w-3 accent-primary"
            checked={!hideUnchanged}
            onChange={(e) => setHideUnchanged(!e.target.checked)}
          />
          Show unchanged sections
        </label>
      </div>
      <div className="flex-1 overflow-y-auto">
        {allUnchanged ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Equal className="h-4 w-4" /> No changes between these versions.
          </p>
        ) : visible.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            All changed sections are hidden. Toggle “Show unchanged sections”.
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {visible.map((s) => (
              <SectionBlock key={s.key} section={s} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const SectionBlock: React.FC<{ section: SectionDiff }> = ({ section }) => {
  const [open, setOpen] = useState(!section.unchanged);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/40 transition"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="text-sm font-medium truncate">{section.title}</span>
        {section.unchanged ? (
          <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
            unchanged
          </span>
        ) : (
          <span className="ml-auto flex items-center gap-2 text-xs tabular-nums">
            <span className="text-emerald-600 dark:text-emerald-400">+{section.added}</span>
            <span className="text-destructive">−{section.removed}</span>
          </span>
        )}
      </button>
      {open && !section.unchanged && (
        <pre className="text-[11px] leading-5 font-mono overflow-x-auto bg-muted/20 border-t border-border/40">
          {section.lines.map((line, i) => (
            <DiffRow key={i} line={line} />
          ))}
        </pre>
      )}
    </li>
  );
};

const DiffRow: React.FC<{ line: DiffLine }> = ({ line }) => {
  const marker = line.op === 'insert' ? '+' : line.op === 'delete' ? '−' : ' ';
  return (
    <div
      className={cn(
        'flex items-start gap-2 px-4 py-0.5',
        line.op === 'insert' && 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
        line.op === 'delete' && 'bg-destructive/10 text-red-900 dark:text-red-100',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'w-4 select-none text-muted-foreground shrink-0',
          line.op === 'insert' && 'text-emerald-600 dark:text-emerald-400',
          line.op === 'delete' && 'text-destructive',
        )}
      >
        {marker}
      </span>
      <span className="whitespace-pre-wrap break-words min-w-0">{line.text || '\u00A0'}</span>
    </div>
  );
};

export default WriterDiffView;
